<template>
    <div class="portfolio-management">
        <!-- Header Actions -->
        <div class="header-actions">
            <div class="search-box">
                <i class="fa-solid fa-search"></i>
                <input 
                    type="text" 
                    v-model="searchTerm" 
                    placeholder="Buscar proyectos..."
                    @input="filterProjects"
                >
            </div>
            <button class="add-btn" @click="openProjectModal()">
                <i class="fa-solid fa-plus"></i>
                Nuevo Proyecto
            </button>
        </div>

        <!-- Projects Cards -->
        <div class="projects-cards">
            <div 
                v-for="project in filteredProjects" 
                :key="project.id" 
                class="project-card"
            >
                <div class="card-image">
                    <img :src="project.image_url" :alt="project.title" class="project-thumbnail">
                </div>
                <div class="card-content">
                    <div class="card-header">
                        <h4>{{ project.title }}</h4>
                        <span class="category-badge" :class="getCategoryClass(project.category_name)">
                            {{ project.category_name }}
                        </span>
                    </div>
                    <p class="subtitle">{{ project.subtitle }}</p>
                    <div class="card-info">
                        <span class="client-label">Cliente:</span>
                        <span class="client-value">{{ project.client }}</span>
                    </div>
                </div>
                <div class="card-actions">
                    <button class="action-btn edit" @click="openProjectModal(project)">
                        <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="action-btn delete" @click="deleteProject(project.id)">
                        <i class="fa-solid fa-trash"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Project Modal -->
        <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h2>{{ editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto' }}</h2>
                    <button class="close-btn" @click="closeModal">&times;</button>
                </div>

                <form @submit.prevent="saveProject" class="project-form">
                    <div class="form-row">
                        <div class="form-group">
                            <label>Título</label>
                            <input type="text" v-model="projectForm.title" required>
                        </div>
                        <div class="form-group">
                            <label>Subtítulo</label>
                            <input type="text" v-model="projectForm.subtitle" required>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label>Categoría</label>
                            <select v-model="projectForm.category_id" required>
                                <option value="">Seleccionar categoría</option>
                                <option v-for="category in categories" :key="category.id" :value="category.id">
                                    {{ category.name }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Cliente</label>
                            <input type="text" v-model="projectForm.client" required>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>URL de la imagen</label>
                        <input type="url" v-model="projectForm.image_url" required>
                    </div>

                    <div class="form-group">
                        <label>Descripción</label>
                        <textarea v-model="projectForm.description" rows="4" required></textarea>
                    </div>

                    <div class="form-group">
                        <label>Tecnologías (separadas por comas)</label>
                        <input type="text" v-model="projectForm.technologiesString" placeholder="HTML5, CSS3, JavaScript">
                    </div>

                    <div class="form-group">
                        <label>Tags (separados por comas)</label>
                        <input type="text" v-model="projectForm.tagsString" placeholder="landing-page, conversion, responsive" required>
                    </div>

                    <div class="form-actions">
                        <button type="button" class="cancel-btn" @click="closeModal">Cancelar</button>
                        <button type="submit" class="save-btn" :disabled="saving">
                            {{ saving ? 'Guardando...' : 'Guardar Proyecto' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
            <div class="modal-content delete-modal" @click.stop>
                <div class="modal-header">
                    <h2>Confirmar Eliminación</h2>
                </div>
                <div class="modal-body">
                    <p>¿Estás seguro de que quieres eliminar el proyecto "{{ projectToDelete?.title }}"?</p>
                    <p>Esta acción no se puede deshacer.</p>
                </div>
                <div class="modal-actions">
                    <button class="cancel-btn" @click="closeDeleteModal">Cancelar</button>
                    <button class="delete-btn" @click="confirmDelete">Eliminar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiService from '../config/api.js';

export default {
    name: 'PortfolioManagement',
    data() {
        return {
            projects: [],
            categories: [],
            searchTerm: '',
            filteredProjects: [],
            showModal: false,
            showDeleteModal: false,
            editingProject: null,
            projectToDelete: null,
            saving: false,
            loading: true,
            error: null,
            projectForm: {
                title: '',
                subtitle: '',
                category_id: '',
                image_url: '',
                description: '',
                client: '',
                technologies: [],
                tags: [],
                technologiesString: '',
                tagsString: '',
                results: {}
            }
        }
    },
    async mounted() {
        await this.loadData();
    },
    methods: {
        async loadData() {
            try {
                this.loading = true;
                this.error = null;
                
                console.log('🚀 Cargando datos del dashboard...');
                
                // Cargar proyectos y categorías en paralelo
                const [projectsResponse, categoriesResponse] = await Promise.all([
                    apiService.getProjects(),
                    apiService.getCategories()
                ]);
                
                console.log('📊 Respuesta de proyectos:', projectsResponse);
                console.log('📋 Respuesta de categorías:', categoriesResponse);
                
                if (projectsResponse && projectsResponse.success) {
                    this.projects = projectsResponse.data || [];
                    this.filteredProjects = [...this.projects];
                    console.log('✅ Proyectos cargados:', this.projects.length);
                } else {
                    const errorMsg = projectsResponse?.error || 'Error al cargar proyectos';
                    console.error('❌ Error en respuesta de proyectos:', projectsResponse);
                    throw new Error(errorMsg);
                }
                
                if (categoriesResponse && categoriesResponse.success) {
                    this.categories = categoriesResponse.data || [];
                    console.log('✅ Categorías cargadas:', this.categories.length);
                } else {
                    const errorMsg = categoriesResponse?.error || 'Error al cargar categorías';
                    console.error('❌ Error en respuesta de categorías:', categoriesResponse);
                    throw new Error(errorMsg);
                }
                
            } catch (error) {
                console.error('❌ Error cargando datos:', error);
                this.error = `Error al cargar los datos: ${error.message}`;
            } finally {
                this.loading = false;
            }
        },
        
        filterProjects() {
            if (!this.searchTerm) {
                this.filteredProjects = this.projects;
                return;
            }

            const term = this.searchTerm.toLowerCase();
            this.filteredProjects = this.projects.filter(project => 
                project.title.toLowerCase().includes(term) ||
                project.subtitle.toLowerCase().includes(term) ||
                project.client.toLowerCase().includes(term) ||
                project.category_name.toLowerCase().includes(term)
            );
        },
        openProjectModal(project = null) {
            this.editingProject = project;
            if (project) {
                const technologies = JSON.parse(project.technologies || '[]');
                const tags = JSON.parse(project.tags || '[]');
                this.projectForm = {
                    title: project.title,
                    subtitle: project.subtitle,
                    category_id: project.category_id,
                    image_url: project.image_url,
                    description: project.description,
                    client: project.client,
                    technologies: technologies,
                    tags: tags,
                    technologiesString: Array.isArray(technologies) ? technologies.join(', ') : '',
                    tagsString: Array.isArray(tags) ? tags.join(', ') : '',
                    results: JSON.parse(project.results || '{}')
                };
            } else {
                this.resetForm();
            }
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
            this.editingProject = null;
            this.resetForm();
        },
        resetForm() {
            this.projectForm = {
                title: '',
                subtitle: '',
                category_id: '',
                image_url: '',
                description: '',
                client: '',
                technologies: [],
                tags: [],
                technologiesString: '',
                tagsString: '',
                results: {}
            };
        },
        async saveProject() {
            this.saving = true;

            try {
                // Convertir strings de tecnologías y tags a arrays
                const technologies = this.projectForm.technologiesString
                    ? this.projectForm.technologiesString.split(',').map(t => t.trim()).filter(t => t)
                    : [];
                const tags = this.projectForm.tagsString
                    ? this.projectForm.tagsString.split(',').map(t => t.trim()).filter(t => t)
                    : [];

                const projectData = {
                    title: this.projectForm.title,
                    subtitle: this.projectForm.subtitle,
                    category_id: this.projectForm.category_id,
                    image_url: this.projectForm.image_url,
                    description: this.projectForm.description,
                    client: this.projectForm.client,
                    duration: null,
                    completion_date: null,
                    technologies: technologies,
                    tags: tags,
                    results: this.projectForm.results,
                    gallery: [],
                    is_featured: false,
                    sort_order: 0
                };

                let response;
                if (this.editingProject) {
                    // Actualizar proyecto existente
                    response = await apiService.updateProject(this.editingProject.id, projectData);
                    console.log('🔄 Actualizando proyecto:', response);
                } else {
                    // Crear nuevo proyecto
                    response = await apiService.createProject(projectData);
                    console.log('➕ Creando proyecto:', response);
                }

                if (response && response.success) {
                    // Recargar datos desde la API
                    await this.loadData();
                    this.closeModal();
                    console.log('✅ Proyecto guardado exitosamente');
                } else {
                    throw new Error(response?.error || 'Error al guardar proyecto');
                }
            } catch (error) {
                console.error('❌ Error al guardar:', error);
                alert(`Error al guardar proyecto: ${error.message}`);
            } finally {
                this.saving = false;
            }
        },
        deleteProject(projectId) {
            this.projectToDelete = this.projects.find(p => p.id === projectId);
            this.showDeleteModal = true;
        },
        closeDeleteModal() {
            this.showDeleteModal = false;
            this.projectToDelete = null;
        },
        async confirmDelete() {
            if (this.projectToDelete) {
                try {
                    const response = await apiService.deleteProject(this.projectToDelete.id);
                    console.log('🗑️ Eliminando proyecto:', response);
                    
                    if (response && response.success) {
                        // Recargar datos desde la API
                        await this.loadData();
                        this.closeDeleteModal();
                        console.log('✅ Proyecto eliminado exitosamente');
                    } else {
                        throw new Error(response?.error || 'Error al eliminar proyecto');
                    }
                } catch (error) {
                    console.error('❌ Error al eliminar:', error);
                    alert(`Error al eliminar proyecto: ${error.message}`);
                }
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES');
        },
        getCategoryClass(category) {
            const classes = {
                'Sitios Web': 'web',
                'Videos': 'video',
                'Imágenes': 'image'
            };
            return classes[category] || 'default';
        }
    }
}
</script>

<style lang="scss" scoped>
.portfolio-management {
    .header-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 30px;
        gap: 20px;

        .search-box {
            position: relative;
            flex: 1;
            max-width: 400px;

            i {
                position: absolute;
                left: 12px;
                top: 50%;
                transform: translateY(-50%);
                color: #666;
            }

            input {
                width: 100%;
                padding: 12px 12px 12px 40px;
                border: 2px solid #e1e5e9;
                border-radius: 8px;
                font-size: 14px;

                &:focus {
                    outline: none;
                    border-color: #1f8cc6;
                }
            }
        }

        .add-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            background: #1f8cc6;
            color: white;
            border: none;
            padding: 12px 20px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 500;
            transition: all 0.3s ease;

            &:hover {
                background: #167aad;
                transform: translateY(-2px);
            }
        }
    }

    .projects-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
        gap: 24px;
        background: none;
        margin-bottom: 40px;
    }

    .project-card {
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        transition: box-shadow 0.2s;
        position: relative;

        &:hover {
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.13);
        }

        .card-image {
            width: 100%;
            height: 180px;
            background: #f8f9fa;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;

            .project-thumbnail {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 0;
            }
        }

        .card-content {
            padding: 20px 20px 10px 20px;
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;

            .card-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 8px;

                h4 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                    flex: 1;
                }

                .category-badge {
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    text-wrap: nowrap;
                    font-weight: 500;
                    margin-left: 10px;

                    &.web {
                        background: #e3f2fd;
                        color: #1f8cc6;
                    }

                    &.video {
                        background: #fff3e0;
                        color: #f57c00;
                    }

                    &.image {
                        background: #f3e5f5;
                        color: #7b1fa2;
                    }

                    &.default {
                        background: #f5f5f5;
                        color: #666;
                    }
                }
            }

            .subtitle {
                margin: 0 0 10px 0;
                font-size: 14px;
                color: #666;
            }

            .card-info {
                display: flex;
                align-items: center;
                font-size: 13px;
                color: #888;
                margin-bottom: 8px;

                .client-label {
                    font-weight: 500;
                    margin-right: 4px;
                }
                .client-value {
                    color: #333;
                }
            }
        }

        .card-actions {
            display: flex;
            gap: 8px;
            padding: 0 20px 16px 20px;

            .action-btn {
                width: 32px;
                height: 32px;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.3s ease;

                &.edit {
                    background: #e3f2fd;
                    color: #1f8cc6;

                    &:hover {
                        background: #1f8cc6;
                        color: white;
                    }
                }

                &.delete {
                    background: #ffebee;
                    color: #f44336;

                    &:hover {
                        background: #f44336;
                        color: white;
                    }
                }
            }
        }
    }

    // Modal styles
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;

        .modal-content {
            background: white;
            border-radius: 12px;
            max-width: 600px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;

            .modal-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 20px 30px;
                border-bottom: 1px solid #e1e5e9;

                h2 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 600;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #666;

                    &:hover {
                        color: #333;
                    }
                }
            }

            .project-form {
                padding: 30px;

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .form-group {
                    margin-bottom: 20px;

                    label {
                        display: block;
                        margin-bottom: 8px;
                        font-weight: 500;
                        color: #333;
                    }

                    input, select, textarea {
                        width: 100%;
                        padding: 12px;
                        border: 2px solid #e1e5e9;
                        border-radius: 8px;
                        font-size: 14px;

                        &:focus {
                            outline: none;
                            border-color: #1f8cc6;
                        }
                    }

                    textarea {
                        resize: vertical;
                        min-height: 80px;
                    }
                }

                .form-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: flex-end;
                    margin-top: 30px;

                    .cancel-btn, .save-btn {
                        padding: 12px 24px;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-weight: 500;
                        transition: all 0.3s ease;
                    }

                    .cancel-btn {
                        background: #f5f5f5;
                        color: #666;

                        &:hover {
                            background: #e0e0e0;
                        }
                    }

                    .save-btn {
                        background: #1f8cc6;
                        color: white;

                        &:hover:not(:disabled) {
                            background: #167aad;
                        }

                        &:disabled {
                            opacity: 0.7;
                            cursor: not-allowed;
                        }
                    }
                }
            }

            &.delete-modal {
                max-width: 400px;

                .modal-body {
                    padding: 20px 30px;

                    p {
                        margin: 0 0 10px 0;
                        color: #666;
                    }
                }

                .modal-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: flex-end;
                    padding: 20px 30px;
                    border-top: 1px solid #e1e5e9;

                    .cancel-btn, .delete-btn {
                        padding: 12px 24px;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-weight: 500;
                    }

                    .cancel-btn {
                        background: #f5f5f5;
                        color: #666;

                        &:hover {
                            background: #e0e0e0;
                        }
                    }

                    .delete-btn {
                        background: #f44336;
                        color: white;

                        &:hover {
                            background: #d32f2f;
                        }
                    }
                }
            }
        }
    }
}

// Dark theme
body.dark-theme .portfolio-management {
    .projects-cards {
        background: none;
    }
    .project-card {
        background: #2b2a2a;
        color: #ffffff;
        box-shadow: 0 4px 20px rgba(0,0,0,0.18);

        .card-content {
            .card-header {
                h4 {
                    color: #ffffff;
                }
            }
            .subtitle {
                color: #ccc;
            }
            .card-info {
                color: #aaa;
                .client-value {
                    color: #fff;
                }
            }
        }
    }

    .modal-content {
        background: #2b2a2a;
        color: #ffffff;

        .modal-header {
            border-bottom-color: #444;

            h2 {
                color: #ffffff;
            }

            .close-btn {
                color: #ccc;

                &:hover {
                    color: #ffffff;
                }
            }
        }

        .project-form {
            .form-group {
                label {
                    color: #ffffff;
                }

                input, select, textarea {
                    background: #1f2122;
                    border-color: #444;
                    color: #ffffff;

                    &:focus {
                        border-color: #f08908;
                    }
                }
            }

            .form-actions {
                .cancel-btn {
                    background: #444;
                    color: #ccc;

                    &:hover {
                        background: #555;
                    }
                }
            }
        }
    }
}

// Responsive
@media (max-width: 768px) {
    .portfolio-management {
        .header-actions {
            flex-direction: column;
            align-items: stretch;

            .search-box {
                max-width: none;
            }
        }

        .projects-cards {
            grid-template-columns: 1fr;
        }

        .modal-content {
            margin: 10px;

            .project-form {
                .form-row {
                    grid-template-columns: 1fr;
                }
            }
        }
    }
}
</style> 